import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  FEEDING_TYPES_KEY,
  getFoodBrandsKey,
  getFoodLineChartKey,
  getFoodTodayKey,
  getFoodsByRangeKey,
  getFoodSizeKey,
  getFoodsKey,
  getListNaturalProductByFoodKey,
  getListProductByFoodKey,
  getMealsByProductKey,
  getMealsKey,
  getPartialsKey,
  getProteinKey,
  getRecentFoodByProductKey,
  getRecentFoodKey,
} from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BasePaginationResponseV2, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { useGetPondNameById } from 'modules/pond/api/selector'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useParams } from 'react-router'
import { useRecoilValue } from 'recoil'

import {
  FOOD_REQUEST_CONFIG,
  getFeedingTypes,
  getFoodBrands,
  getFoodSizes,
  getFoodToday,
  getListNaturalProductByFood,
  getListProductByFood,
  getProteins,
} from '.'
import {
  selectedMealDateState,
  selectedMealIdState,
  selectedTableDateState,
  selectedTableFeedingTypeState,
} from '../stores/atom'
import {
  FoodFull,
  FoodLineChart,
  Meal,
  MealByProduct,
  Partial,
  RecentFood,
  RecentFoodByProduct,
} from './types'

const ROWS_PER_PAGE = 10

export const useGetMeals = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const date = useRecoilValue(selectedMealDateState)
  const dateStr = formatDate(date)

  return useQuery({
    queryKey: getMealsKey.list(dateStr, cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<Meal[]>>({
        ...FOOD_REQUEST_CONFIG.getMeals,
        params: {
          date: dateStr,
        },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetMealsByProduct = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const mealId = useRecoilValue(selectedMealIdState)

  return useQuery({
    queryKey: getMealsByProductKey.details(cycleId || '', mealId || 0),
    queryFn: async () => {
      if (!mealId) return
      const { data } = await cycleApi.request<BaseResponse<MealByProduct>>({
        ...FOOD_REQUEST_CONFIG.getMealsByProduct,
        url: `${FOOD_REQUEST_CONFIG.getMealsByProduct.url}/${mealId}`,
      })

      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && !!mealId,
  })
}

export const useGetFoodSizes = (foodBrandId: number, proteinId: number) => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')
  const speciesId = pond?.species.id
  const isEnabled = foodBrandId !== -1 && proteinId !== -1 && !!speciesId
  return useQuery({
    queryKey: getFoodSizeKey.list(foodBrandId, proteinId, speciesId),
    queryFn: async () => {
      const { data } = await getFoodSizes({ foodBrandId, proteinId, speciesId: speciesId ?? 0 })
      return data.data
    },
    staleTime: Infinity,
    enabled: isEnabled,
  })
}

export const useGetFeedingTypes = () => {
  return useQuery({
    queryKey: FEEDING_TYPES_KEY,
    queryFn: async () => {
      const { data } = await getFeedingTypes()
      return data.data
    },
    staleTime: Infinity,
  })
}

export const useGetFoodLineChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getFoodLineChartKey.details(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<FoodLineChart[]>>(
        FOOD_REQUEST_CONFIG.getFoodLineChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetFoods = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const date = useRecoilValue(selectedTableDateState)
  const feedingTypeId = useRecoilValue(selectedTableFeedingTypeState)

  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,
      feedingTypeId: feedingTypeId !== -1 ? feedingTypeId : '',
      date: date ? formatDate(date) : '',
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getFoodsKey.list({ cycleId: cycleId || '', date, feedingType: feedingTypeId }),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<FoodFull[]>>({
        ...FOOD_REQUEST_CONFIG.getFoods,
        ...pageParam,
      })
      return {
        foods: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage, _) => {
      if (lastPage.nextPage) {
        return {
          cycleId: initParams.cycleId,
          params: { ...initParams.params, page: lastPage.nextPage },
        }
      } else {
        return undefined
      }
    },
    staleTime: Infinity,
    enabled: !!cycleId && !!feedingTypeId,
  })
}

export const useGetFoodsByRange = ({
  startDate,
  endDate,
}: {
  startDate?: Date
  endDate?: Date
}) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const feedingTypeId = useRecoilValue(selectedTableFeedingTypeState)

  const initParams = {
    params: {
      pageSize: ROWS_PER_PAGE,
      page: 1,
      feedingTypeId: feedingTypeId !== -1 ? feedingTypeId : '',
      startDate: startDate ? formatDate(startDate) : undefined,
      endDate: endDate ? formatDate(endDate) : undefined,
    },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: getFoodsByRangeKey.list({
      cycleId: cycleId || '',
      startDate,
      endDate,
      feedingType: feedingTypeId,
    }),
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<FoodFull[]>>({
        ...FOOD_REQUEST_CONFIG.getFoods,
        ...pageParam,
      })
      return {
        foods: data.data,
        nextPage: data.nextPage,
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) {
        return {
          cycleId: initParams.cycleId,
          params: { ...initParams.params, page: lastPage.nextPage },
        }
      }
      return undefined
    },
    staleTime: Infinity,
    enabled: !!cycleId && !!feedingTypeId,
  })
}

export const useGetProteins = (foodbrandId: number) => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')
  const speciesId = pond?.species.id ?? 0
  return useQuery({
    queryKey: getProteinKey(foodbrandId, speciesId),
    queryFn: async () => {
      const { data } = await getProteins(foodbrandId, speciesId)
      return data.data
    },
    staleTime: Infinity,
    enabled: foodbrandId !== -1,
  })
}

export const useGetRecentFood = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getRecentFoodKey.details(cycleId ?? ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<RecentFood>>(
        FOOD_REQUEST_CONFIG.getRecentFood
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetRecentFoodByProduct = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getRecentFoodByProductKey.byCycleId(cycleId ?? ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<RecentFoodByProduct>>(
        FOOD_REQUEST_CONFIG.getRecentFoodByProduct
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetFoodBrands = () => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  return useQuery({
    queryKey: getFoodBrandsKey(pond?.species.id),
    queryFn: async () => {
      const { data } = await getFoodBrands(pond?.species.id ?? 0)
      return data.data
    },
    staleTime: Infinity,
    enabled: !!pond,
  })
}

export const useGetPartials = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getPartialsKey.list(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<Partial>>(FOOD_REQUEST_CONFIG.partials)
      if (data.data) {
        return data.data.partials
      }
      return []
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetFoodToday = () => {
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId, farmId } = useParams()

  return useQuery({
    queryKey: getFoodTodayKey.details(farmId || '', pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data } = await getFoodToday(farmId ?? '', pondId ?? '', cycleId ?? '')
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
  })
}

export const useGetListProductByFood = () => {
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId, farmId } = useParams()

  return useQuery({
    queryKey: getListProductByFoodKey(farmId || '', pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data } = await getListProductByFood(farmId ?? '', pondId ?? '', cycleId ?? '')
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}

export const useGetListProductByNaturalFood = () => {
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { pondId, farmId } = useParams()

  return useQuery({
    queryKey: getListNaturalProductByFoodKey(farmId || '', pondId || '', cycleId || ''),
    queryFn: async () => {
      const { data } = await getListNaturalProductByFood(farmId ?? '', pondId ?? '', cycleId ?? '')
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId,
    refetchOnMount: 'always',
  })
}
